import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../Utility/AuthProvider";
import APICall from "../../Utility/APICall";
import { paths } from "../../Utility/Constants";
import GenericTable from "../GenericTable";
import loaderAnimation from "../../assets/animations/loader.json";
import FilterBuildingModal from "./FilterBuildingModal"; // Ensure this is styled with Tailwind
import Lottie from "lottie-react";

function Units() {
  const { jwt } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const buildingIdFromUrl = searchParams.get("buildingId");

  const [units, setUnits] = useState([]);
  const [allBuildings, setAllBuildings] = useState([]);
  const [nextPageToken, setNextPageToken] = useState(null); // Will remain null if API doesn't send it
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false); // Will likely not be used if API sends all data
  const [error, setError] = useState("");
  const [hasMore, setHasMore] = useState(true); // Will become false after first fetch if no token

  const [filterBuildingModalVisible, setFilterBuildingModalVisible] =
    useState(false);
  const [currentFilterBuildingId, setCurrentFilterBuildingId] =
    useState(buildingIdFromUrl);

  const observer = useRef(); // Still useful if API pagination is added later

  useEffect(() => {
    const fetchAllBuildingsForFilter = async () => {
      try {
        const buildingResponse = await APICall(
          "GET",
          paths.building.fetch,
          undefined,
          undefined,
          jwt
        );
        setAllBuildings(buildingResponse.response || []);
      } catch (err) {
        console.error("Error fetching buildings for filter:", err);
        setAllBuildings([]);
      }
    };
    if (jwt) {
      fetchAllBuildingsForFilter();
    }
  }, [jwt]);

  const processUnits = (newUnits) => {
    return newUnits.map((unit) => ({
      ...unit,
      id:
        unit.entity_id ||
        `unit-${Date.now()}-${Math.random().toString(16).slice(2)}`, // More robust fallback key
    }));
  };

  const fetchUnitsData = useCallback(
    async (token, buildingFilterForFetch) => {
      // If a token is passed but API doesn't actually use it, this part is fine.
      // The important part is how we handle the RESPONSE.
      if (!token) {
        setLoadingInitial(true);
      } else {
        // This state might not be hit if the API doesn't paginate
        setLoadingMore(true);
      }
      setError("");

      try {
        let apiUrl = paths.unit.fetch;
        const queryParams = new URLSearchParams();

        if (buildingFilterForFetch) {
          queryParams.append("building_id", buildingFilterForFetch);
        }
        // If API supports pagination via token in future, this will be used:
        if (token) {
          queryParams.append("next_page_token", token);
        }

        if (queryParams.toString()) {
          apiUrl += `?${queryParams.toString()}`;
        }

        const unitResponse = await APICall(
          "GET",
          apiUrl,
          undefined,
          undefined,
          jwt
        );
        console.log("Units API Full Response:", unitResponse);

        // Corrected data extraction based on your log
        if (
          unitResponse &&
          unitResponse.status === "success" &&
          Array.isArray(unitResponse.response)
        ) {
          const fetchedUnits = processUnits(unitResponse.response); // unitResponse.response IS the array
          setUnits((prevUnits) =>
            token ? [...prevUnits, ...fetchedUnits] : fetchedUnits
          ); // Appends if token, replaces if no token

          // Check for next_page_token in the response.
          // THIS IS WHERE YOUR API NEEDS TO PROVIDE IT FOR PAGINATION.
          const apiNextPageToken =
            unitResponse.next_page_token ||
            unitResponse.metadata?.next_page_token ||
            null;
          setNextPageToken(apiNextPageToken);
          setHasMore(!!apiNextPageToken); // If no token, hasMore will be false

          if (!apiNextPageToken) {
            console.log(
              "No next_page_token found in Units API response. All current data displayed."
            );
          }
        } else {
          console.error(
            "Unexpected API response structure for units OR API status not success:",
            unitResponse
          );
          setError(unitResponse?.message || "Failed to parse unit data.");
          setHasMore(false);
        }
      } catch (err) {
        console.error("Error fetching units:", err);
        setError(err.message || "Failed to fetch units");
        setHasMore(false);
      } finally {
        setLoadingInitial(false);
        setLoadingMore(false);
      }
    },
    [jwt]
  );

  useEffect(() => {
    if (jwt) {
      setUnits([]);
      setNextPageToken(null);
      setHasMore(true);
      fetchUnitsData(null, currentFilterBuildingId);
    }
  }, [jwt, currentFilterBuildingId, fetchUnitsData]);

  const lastUnitElementRef = useCallback(
    (node) => {
      if (loadingInitial || loadingMore) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore && nextPageToken) {
          fetchUnitsData(nextPageToken, currentFilterBuildingId);
        }
      });
      if (node) observer.current.observe(node);
    },
    [
      loadingInitial,
      loadingMore,
      hasMore,
      nextPageToken,
      currentFilterBuildingId,
      fetchUnitsData,
    ]
  );

  // const handleEditUnitClick = (unitRowClicked) => {
  //   const unitToNavigate = units.find((u) => u.id === unitRowClicked.id);
  //   if (unitToNavigate) {
  //     // navigate(`/MyProperties/${unitToNavigate.unit_name}`, {
  //     //   state: unitToNavigate,
  //     // });
  //     navigate(`/dashboard/my-properties/${unitToNavigate.unit_name}`, {
  //       state: unitToNavigate,
  //     });
  //   }
  // };

  const handleEditUnitClick = (unitRowClicked) => {
    // Assuming unitRowClicked already contains entity_id or unit.id is the entity_id
    // Your `processUnits` sets `id: unit.entity_id`
    const unitIdForPath = unitRowClicked.id; // This should be the entity_id

    if (unitIdForPath) {
      // Find the full unit object from `units` state to pass in `location.state`
      // This ensures the most complete object is passed if `unitRowClicked` is partial.
      const unitToPassInState = units.find((u) => u.id === unitIdForPath);

      console.log(
        "Units.jsx: Navigating to UnitDetails with ID:",
        unitIdForPath,
        "and state:",
        unitToPassInState
      );
      navigate(`/dashboard/my-properties/${unitIdForPath}`, {
        // Use the actual ID in the URL
        state: { unit: unitToPassInState }, // Pass the full unit object in state
      });
    } else {
      console.error(
        "Units.jsx: Cannot navigate, unit ID (entity_id) is missing from clicked row.",
        unitRowClicked
      );
      alert("Error: Could not retrieve unit details. ID missing.");
    }
  };

  const handleApplyBuildingFilterFromModal = (buildingIdToFilter) => {
    setFilterBuildingModalVisible(false);
    const newParams = new URLSearchParams(searchParams);
    if (buildingIdToFilter) {
      newParams.set("buildingId", buildingIdToFilter);
    } else {
      newParams.delete("buildingId");
    }
    setSearchParams(newParams);
    setCurrentFilterBuildingId(buildingIdToFilter);
  };

  const clearActiveFilter = () => {
    handleApplyBuildingFilterFromModal(null);
  };

  const columns = [
    { id: "serialNo", label: "Record No.", minWidth: 80 },
    { id: "unitName", label: "Unit Name", minWidth: 150 },
    { id: "buildingName", label: "Building Name", minWidth: 170 },
    { id: "address", label: "Address", minWidth: 200 },
    { id: "furniture", label: "Furniture", minWidth: 100 },
    { id: "bhk", label: "BHK", minWidth: 70 },
    { id: "status", label: "Status", minWidth: 100 },
  ];

  const rows = units.map((unit, index) => ({
    ...unit, // Includes the 'id' from processUnits
    serialNo: index + 1,
    unitName: unit.unit_name || "N/A",
    buildingName: unit.building_name || "N/A",
    address: unit.address || "N/A",
    furniture: unit.furnishing_status || "N/A",
    bhk: unit.bhk || "N/A",
    // status: unit.rent_status || unit.occupancy_status || "N/A",

    status: (
      <span
        style={{
          color:
            unit.occupancy_status === "rented"
              ? "green"
              : unit.occupancy_status === "vacant"
                ? "red"
                : "inherit",
          // fontWeight: "bold",
          // marginLeft: "5px", // Add some spacing if needed
        }}
      >
        {unit.occupancy_status === "rented"
          ? "Rented"
          : unit.occupancy_status === "vacant"
            ? "Vacant"
            : "N/A"}
      </span>
    ),
  }));

  return (
    <div className="bg-white rounded-lg shadow-md p-4 md:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
        <h1 className="text-xl font-bold text-gray-800">Units</h1>
        <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
          <button
            onClick={() => setFilterBuildingModalVisible(true)}
            className="bg-blue-500 text-white text-sm px-4 py-2 rounded-md hover:bg-blue-600 transition-colors w-full sm:w-auto"
          >
            Filter by Building
          </button>
          <button
            onClick={clearActiveFilter}
            disabled={!currentFilterBuildingId}
            className={`text-sm px-4 py-2 rounded-md transition-colors w-full sm:w-auto ${
              currentFilterBuildingId
                ? "bg-gray-500 text-white hover:bg-gray-600"
                : "bg-gray-300 text-gray-400 cursor-not-allowed"
            }`}
          >
            Clear Filter
          </button>
        </div>
      </div>

      <GenericTable
        columns={columns}
        rows={rows}
        rowKeyField="id"
        loading={loadingInitial && units.length === 0}
        loaderAnimation={loaderAnimation}
        onRowClick={handleEditUnitClick}
      />

      {/* "Loading More" indicator - will only show if API starts sending tokens */}
      {loadingMore && (
        <div className="flex justify-center py-4 mt-2">
          <Lottie
            animationData={loaderAnimation}
            loop={true}
            style={{ height: 60 }}
          />
        </div>
      )}

      {/* Invisible trigger for IntersectionObserver - will only be effective if API sends tokens */}
      {hasMore &&
        !loadingInitial &&
        !loadingMore &&
        units.length > 0 &&
        nextPageToken && (
          <div
            ref={lastUnitElementRef}
            style={{ height: "1px", marginTop: "1px" }}
          />
        )}

      {/* Message when all data (for current filter) is loaded, especially if API doesn't paginate */}
      {!loadingInitial && !hasMore && units.length > 0 && (
        <p className="text-center text-gray-500 py-4 text-sm">
          All {units.length} unit{units.length === 1 ? "" : "s"} loaded.
        </p>
      )}

      {/* Error display / No data message */}
      {error && !loadingInitial && units.length === 0 && (
        <p className="text-red-500 text-center py-10 text-lg">{error}</p>
      )}
      {!loadingInitial && !loadingMore && units.length === 0 && !error && (
        <p className="text-gray-500 text-center py-10">
          {currentFilterBuildingId
            ? "No units found for the selected building."
            : "No units available."}
        </p>
      )}

      <FilterBuildingModal
        show={filterBuildingModalVisible}
        handleClose={() => setFilterBuildingModalVisible(false)}
        buildings={allBuildings}
        selectedBuildingId={currentFilterBuildingId}
        handleApply={handleApplyBuildingFilterFromModal}
      />
    </div>
  );
}

export default Units;
